/* global define */
/* eslint-disable no-var, object-shorthand */
/**
 * Theme resolution.
 *
 * Turning a form's appearance settings into the six CSS custom properties the
 * stylesheet reads is pure mapping: settings in, values out, no DOM and no view
 * state. Keeping it here means the resolution rules — which setting wins, what
 * a missing value falls back to, how each background mode is built — can be
 * read on their own, and the view is left with the one job the DOM requires,
 * which is writing the result onto the right elements.
 *
 * This is also where the values stop being trusted. Whatever the settings hold,
 * what leaves here is a color the add-on could itself have saved, a background
 * mode it knows, an angle inside the slider's range, and a media URL a
 * stylesheet may fetch. The builder already holds the owner to that grammar,
 * but a value can reach storage without passing through it — a `.nff` import,
 * the native setting rows the panel hides but leaves live, a direct form-meta
 * write, a third-party filter — and everything resolved here is painted onto
 * every visitor's page. The server applies the same rules before the settings
 * are handed to the display; both boundaries are owned by the add-on, and
 * neither is entitled to assume the other ran.
 *
 * Owner-configured colors resolve exactly as chosen. Color accessibility beyond
 * the shipped defaults is the owner's call, guided by the plain-language
 * warning in the appearance editor rather than overridden here: normalizing a
 * color changes only its notation, never which color it is.
 */
define( 'models/theme',[ 'shared/background', 'shared/contrast' ], function (
	cfBackground,
	cfContrast
) {
	/**
	 * Read one setting as a color that is safe to paint.
	 *
	 * A value that is not a hex color is not a color this add-on wrote, so the
	 * shipped default is painted instead. An empty result removes the custom
	 * property, which leaves the stylesheet's own fallback in charge.
	 *
	 * @param {Function} setting  Reads a form setting with a fallback.
	 * @param {Object}   defaults The localized shipped defaults.
	 * @param {string}   name     Setting name.
	 * @return {string} A #rrggbb color, or '' when even the default is unusable.
	 */
	function color( setting, defaults, name ) {
		return (
			cfContrast.normalize( setting( name, '' ) ) ||
			cfContrast.normalize( defaults[ name ] ) ||
			''
		);
	}

	/**
	 * Wrap a setting reader so the background resolver only sees valid values.
	 *
	 * The resolver whitelists the background mode and gradient type and clamps
	 * the angle itself, since it owns what those mean; the colors it composes
	 * are normalized here, where the color rules already live.
	 *
	 * @param {Function} setting  Reads a form setting with a fallback.
	 * @param {Object}   defaults The localized shipped defaults.
	 * @return {Function} A reader with the same signature.
	 */
	function guarded( setting, defaults ) {
		return function ( name, fallback ) {
			if ( -1 !== cfBackground.colorSettings.indexOf( name ) ) {
				return color( setting, defaults, name ) || fallback;
			}

			return setting( name, fallback );
		};
	}

	/**
	 * Resolve a form's appearance settings to CSS custom properties.
	 *
	 * @param {Function} setting      Reads a form setting with a fallback.
	 * @param {Object}   defaults     The localized shipped defaults.
	 * @param {Function} resolveMedia Resolves a media setting to a URL.
	 * @return {Object} Custom property name to value.
	 */
	function properties( setting, defaults, resolveMedia ) {
		var read = guarded( setting, defaults );

		return {
			'--nf-cf-background': cfBackground.resolve(
				read,
				defaults,
				resolveMedia
			),
			'--nf-cf-text': color(
				setting,
				defaults,
				'conversational_text_color'
			),
			'--nf-cf-field-text': color(
				setting,
				defaults,
				'conversational_field_text_color'
			),
			'--nf-cf-accent': color(
				setting,
				defaults,
				'conversational_accent_color'
			),
			'--nf-cf-control': color(
				setting,
				defaults,
				'conversational_control_color'
			),
			'--nf-cf-validation': color(
				setting,
				defaults,
				'conversational_validation_color'
			),
		};
	}

	/**
	 * Read the URL out of a media setting, which may be a string or an object.
	 *
	 * Whether the URL is one the theme may paint is decided where it is
	 * composed into CSS, so this stays a plain read of the stored shape.
	 *
	 * @param {string|Object} value The stored media setting.
	 * @return {string} The URL, or an empty string.
	 */
	function mediaUrl( value ) {
		if ( 'string' === typeof value ) {
			return value;
		}

		return value && value.url ? value.url : '';
	}

	return {
		properties: properties,
		mediaUrl: mediaUrl,
	};
} );

